/**
 * PreAuthRemainderConfirmPage — Buyer confirms an off-session top-up that
 * required SCA. Reached via push/notification when the seller chose
 * `outcome=charge` with Y > X and the buyer's bank required authentication
 * for the remainder portion.
 *
 * Route: /preauth/:preauth_id/remainder/confirm
 *
 * Flow:
 *  1. GET /preauth/:id → reads remainder_client_secret
 *  2. If absent (already confirmed / never required SCA) → redirect to chat
 *  3. stripe.confirmCardPayment(remainderClientSecret) — buyer completes 3DS
 *  4. On success → toast + redirect to /messages/:matchId. The webhook
 *     (payment_intent.succeeded with metadata.kind=preauth_remainder) flips
 *     remainder_status → succeeded and credits the seller.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import { ShieldAlert, ArrowLeft, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

export default function PreAuthRemainderConfirmPage() {
  const { preauth_id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [preauth, setPreauth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/preauth/${preauth_id}`);
        const pa = res.data;

        // If the remainder already succeeded / was never needed, skip the page.
        if (pa.remainder_status === 'succeeded' || !pa.remainder_client_secret) {
          const matchId = pa.match_id;
          navigate(matchId ? `/messages/${matchId}` : '/messages', { replace: true });
          return;
        }

        if (pa.status !== 'remainder_action_required') {
          setError(`This top-up is no longer pending (status: ${pa.status}).`);
          setLoading(false);
          return;
        }

        setPreauth(pa);
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(detail || 'Failed to load top-up details.');
      } finally {
        setLoading(false);
      }
    })();
  }, [preauth_id, api, navigate]);

  const handleConfirm = async () => {
    if (!preauth?.remainder_client_secret) return;
    setConfirming(true);
    setError('');

    const stripe = await stripePromise;
    const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
      preauth.remainder_client_secret
    );

    if (confirmError) {
      setError(confirmError.message || 'Top-up confirmation failed. Please try again.');
      setConfirming(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      setConfirmed(true);
      toast.success('Top-up confirmed — payment complete');
      setTimeout(() => {
        navigate(preauth.match_id ? `/messages/${preauth.match_id}` : '/messages', { replace: true });
      }, 2000);
    } else {
      setError(`Unexpected status: ${paymentIntent?.status}.`);
      setConfirming(false);
    }
  };

  const backHref = preauth?.match_id ? `/messages/${preauth.match_id}` : '/messages';
  const remainderDisplay = preauth?.remainder_cents != null
    ? `$${(preauth.remainder_cents / 100).toFixed(2)} AUD`
    : '';

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-8">
        <Link
          to={backHref}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to chat
        </Link>

        <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[var(--theme-text-primary)]">
                Confirm Top-up
              </h1>
              <p className="text-xs text-[var(--theme-text-muted)]">
                Your bank requires confirmation
              </p>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--theme-text-muted)]" />
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 p-4 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-700 dark:text-red-400">{error}</p>
                <Link
                  to={backHref}
                  className="text-xs text-red-600 dark:text-red-500 underline mt-1 inline-block"
                >
                  Return to chat
                </Link>
              </div>
            </div>
          )}

          {!loading && confirmed && (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
                <ShieldCheck className="w-7 h-7 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-base font-semibold text-[var(--theme-text-primary)]">
                Top-up confirmed
              </p>
              <p className="text-sm text-[var(--theme-text-muted)] mt-1">
                Returning to chat…
              </p>
            </div>
          )}

          {!loading && !error && !confirmed && preauth && (
            <div className="space-y-5">
              <div className="rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/40 p-4">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-900 dark:text-amber-200 text-sm">
                      {remainderDisplay} top-up
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 leading-relaxed">
                      Your bank needs you to confirm this charge on your saved
                      card. You may be redirected to your bank's confirmation
                      page.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleConfirm}
                disabled={confirming}
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white h-11"
              >
                {confirming
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : <ShieldAlert className="w-4 h-4 mr-2" />}
                {confirming ? 'Confirming…' : `Confirm ${remainderDisplay}`}
              </Button>

              <p className="text-xs text-center text-[var(--theme-text-muted)]">
                Once confirmed, the charge completes immediately.
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
